import { useState, useEffect, useMemo } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Eye, EyeOff, Loader2, CheckCircle2, AlertTriangle } from "lucide-react";

export default function ResetPasswordPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = useMemo(() => params.get("token") || "", [params]);

  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (!token) {
      toast.error("Lien de réinitialisation invalide");
    }
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (password.length < 8) {
      toast.error("Le mot de passe doit contenir au moins 8 caractères");
      return;
    }
    if (password !== confirm) {
      toast.error("Les mots de passe ne correspondent pas");
      return;
    }

    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: password });
      setDone(true);
      toast.success("Mot de passe mis à jour !");
    } catch (error) {
      const message = error.response?.data?.detail || "Impossible de réinitialiser le mot de passe";
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  if (!token) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 p-6">
        <div className="max-w-md w-full bg-white p-8 rounded-xl shadow-sm border">
          <div className="flex items-center gap-3 text-red-600 mb-4">
            <AlertTriangle className="w-6 h-6" />
            <h1 className="text-xl font-bold">Lien invalide</h1>
          </div>
          <p className="text-gray-600 mb-6">Le lien de réinitialisation est manquant ou invalide.</p>
          <Button
            className="w-full btn-primary"
            onClick={() => navigate("/login")}
            data-testid="reset-back-to-login-btn"
          >
            Retour à la connexion
          </Button>
        </div>
      </div>
    );
  }

  if (done) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 p-6">
        <div className="max-w-md w-full bg-white p-8 rounded-xl shadow-sm border">
          <div className="flex items-center gap-3 text-green-600 mb-4">
            <CheckCircle2 className="w-6 h-6" />
            <h1 className="text-xl font-bold">Mot de passe mis à jour</h1>
          </div>
          <p className="text-gray-600 mb-6">
            Votre nouveau mot de passe a bien été enregistré. Vous pouvez maintenant vous connecter.
          </p>
          <Button
            className="w-full btn-primary"
            onClick={() => navigate("/login")}
            data-testid="reset-go-to-login-btn"
          >
            Se connecter
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 p-6">
      <div className="max-w-md w-full bg-white p-8 rounded-xl shadow-sm border" data-testid="reset-password-page">
        <h1 className="text-2xl font-bold text-[#212E38] mb-2" style={{ fontFamily: "Chivo, sans-serif" }}>
          Nouveau mot de passe
        </h1>
        <p className="text-gray-500 mb-6 text-sm">
          Choisissez un nouveau mot de passe (8 caractères minimum).
        </p>

        <form onSubmit={handleSubmit} className="space-y-5">
          <div className="space-y-2">
            <Label htmlFor="new-password">Nouveau mot de passe</Label>
            <div className="relative">
              <Input
                id="new-password"
                type={showPassword ? "text" : "password"}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="h-12 bg-gray-50 border-gray-200 focus:bg-white pr-12"
                placeholder="••••••••"
                required
                minLength={8}
                autoComplete="new-password"
                data-testid="reset-new-password-input"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
              </button>
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="confirm-password">Confirmer le mot de passe</Label>
            <Input
              id="confirm-password"
              type={showPassword ? "text" : "password"}
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              className="h-12 bg-gray-50 border-gray-200 focus:bg-white"
              placeholder="••••••••"
              required
              minLength={8}
              autoComplete="new-password"
              data-testid="reset-confirm-password-input"
            />
          </div>

          <Button
            type="submit"
            className="w-full h-12 btn-primary text-base"
            disabled={loading}
            data-testid="reset-submit-btn"
          >
            {loading ? (
              <>
                <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                Enregistrement...
              </>
            ) : (
              "Mettre à jour le mot de passe"
            )}
          </Button>

          <Button
            type="button"
            variant="ghost"
            className="w-full"
            onClick={() => navigate("/login")}
            data-testid="reset-cancel-btn"
          >
            Annuler
          </Button>
        </form>
      </div>
    </div>
  );
}
